import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, List, ListItem, ListItemButton, ListItemText, ListItemAvatar, ListItemIcon,
  Avatar, IconButton, CircularProgress, Divider, Menu, MenuItem, Tooltip, Chip,
  TextField, InputAdornment, Autocomplete, Fab, Collapse,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ShareIcon from '@mui/icons-material/Share';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../components/markdownComponents';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserItems, deleteItem, refreshItem, updateItemFlags } from '../services/items';
import { getUsersByIds } from '../services/users';
import {
  getSharedByMe, getSharedWithMe, moveShareToFolder,
  getShareFolders, createShareFolder, updateShareFolder, deleteShareFolder,
  type ShareHistoryEntry, type SharedWithMeEntry,
} from '../services/shares';
import type { Item, ShareFolder } from '../types';
import { ItemFlags, hasFlag } from '../types';
import ItemsView, { ViewModeToggle, type ViewMode } from '../components/items/ItemsView';
import CreateItemDialog from '../components/items/CreateItemDialog';
import EditItemDialog from '../components/items/EditItemDialog';
import AddToFolderDialog from '../components/folders/AddToFolderDialog';
import { streamItemsSummary } from '../services/items';
import ShareDialog from '../components/sharing/ShareDialog';
import RenameDialog from '../components/documents/RenameDialog';

type ShareView = 'all' | 'with-me' | 'by-me' | string;

// Composite icon: folder + small share badge
function SharedWithMeIcon({ open }: { open?: boolean }) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', width: 20, height: 20 }}>
      {open
        ? <FolderOpenIcon fontSize="small" sx={{ color: 'primary.main' }} />
        : <FolderIcon fontSize="small" sx={{ color: 'primary.main' }} />}
      <ShareIcon sx={{ position: 'absolute', bottom: -2, right: -4, fontSize: 11, color: 'primary.main', bgcolor: 'background.paper', borderRadius: '50%', p: '1px' }} />
    </Box>
  );
}

function SharedByMeIcon({ open }: { open?: boolean }) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', width: 20, height: 20 }}>
      {open
        ? <FolderOpenIcon fontSize="small" sx={{ color: 'success.main' }} />
        : <FolderIcon fontSize="small" sx={{ color: 'success.main' }} />}
      <ShareIcon sx={{ position: 'absolute', bottom: -2, right: -4, fontSize: 11, color: 'success.main', bgcolor: 'background.paper', borderRadius: '50%', p: '1px' }} />
    </Box>
  );
}

export default function SharesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // --- Sidebar state ---
  const [view, setView] = useState<ShareView>('all');
  const [shareFolders, setShareFolders] = useState<ShareFolder[]>([]);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState<null | HTMLElement>(null);
  const [folderMenuTarget, setFolderMenuTarget] = useState<ShareFolder | null>(null);
  const [renameFolder, setRenameFolder] = useState<ShareFolder | null>(null);
  const [moveEntryTarget, setMoveEntryTarget] = useState<ShareHistoryEntry | null>(null);
  const [foldersExpanded, setFoldersExpanded] = useState(() => localStorage.getItem('shares_foldersExpanded') !== 'false');
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('shares_sidebarOpen') !== 'false');

  // --- All items state (for 'all' view) ---
  const [items, setItems] = useState<Item[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [creatorNames, setCreatorNames] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<ViewMode>(() => { const stored = localStorage.getItem('itemsViewMode'); return stored === 'card' || stored === 'list' ? stored : 'card'; });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState<string | null>(searchParams.get('tag'));
  const [filterSite, setFilterSite] = useState<string | null>(null);
  const [filterStarred, setFilterStarred] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<Item | null>(null);
  const [folderItem, setFolderItem] = useState<Item | null>(null);
  const [shareItem, setShareItem] = useState<Item | null>(null);

  // Summary state
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [summaryStreaming, setSummaryStreaming] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const summaryAbortRef = useRef<AbortController | null>(null);

  // --- Share list state (for 'with-me', 'by-me', folder views) ---
  const [loading, setLoading] = useState(false);
  const [byMeEntries, setByMeEntries] = useState<ShareHistoryEntry[]>([]);
  const [withMeEntries, setWithMeEntries] = useState<SharedWithMeEntry[]>([]);

  const loadShareFolders = useCallback(async () => {
    getShareFolders().then(setShareFolders).catch(() => {});
  }, []);

  useEffect(() => { loadShareFolders(); }, [loadShareFolders]);

  // Load items for 'all' view
  const loadItems = useCallback(async () => {
    if (!user) return;
    setLoadingItems(true);
    try {
      const result = await getUserItems(user.uid);
      setItems(result);
      // Load creator names for any unknown UIDs
      const unknownUids = [...new Set(result.map(i => i.createdBy))].filter(uid => !creatorNames[uid]);
      if (unknownUids.length > 0) {
        const users = await getUsersByIds(unknownUids);
        const nameMap: Record<string, string> = {};
        users.forEach(u => { nameMap[u.uid] = u.displayName; });
        setCreatorNames(prev => ({ ...prev, ...nameMap }));
      }
    } finally {
      setLoadingItems(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (view === 'all') loadItems();
  }, [view, loadItems]);


  // Load share lists for non-all views
  const loadShareList = useCallback(async () => {
    if (view === 'all') return;
    setLoading(true);
    try {
      if (view === 'with-me') {
        setWithMeEntries(await getSharedWithMe());
      } else {
        const opts =
          view === 'by-me' ? undefined :
          { folderId: view };
        setByMeEntries(await getSharedByMe(opts));
      }
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { loadShareList(); }, [loadShareList]);

  const handleDeleteFolder = async (folder: ShareFolder) => {
    if (!confirm(`Delete folder "${folder.name}"? Shares will become ungrouped.`)) return;
    await deleteShareFolder(folder.id);
    setShareFolders(prev => prev.filter(f => f.id !== folder.id));
    if (view === folder.id) setView('by-me');
  };

  // Filtered items for 'all' view
  const allTags = useMemo(() => {
    const s = new Set<string>();
    items.forEach(i => { i.tags.forEach(t => s.add(t)); i.autoTags.forEach(t => s.add(t)); });
    return [...s].sort();
  }, [items]);
  const allSites = useMemo(() => {
    const s = new Set<string>();
    items.forEach(i => { if (i.site) s.add(i.site); });
    return [...s].sort();
  }, [items]);
  const filteredItems = useMemo(() => {
    let r = items;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      r = r.filter(i =>
        i.title.toLowerCase().includes(q) || i.summary.toLowerCase().includes(q) ||
        i.content.toLowerCase().includes(q) || i.tags.some(t => t.toLowerCase().includes(q)) ||
        i.autoTags.some(t => t.toLowerCase().includes(q))
      );
    }
    if (filterTag) r = r.filter(i => i.tags.includes(filterTag) || i.autoTags.includes(filterTag));
    if (filterSite) r = r.filter(i => i.site === filterSite);
    if (filterStarred) r = r.filter(i => hasFlag(i.flags, ItemFlags.STARRED));
    return r;
  }, [items, searchQuery, filterTag, filterSite, filterStarred]);

  const handleSummary = useCallback(async () => {
    if (filteredItems.length === 0) return;
    summaryAbortRef.current?.abort();
    const controller = new AbortController();
    summaryAbortRef.current = controller;
    setSummaryOpen(true);
    setSummaryText('');
    setSummaryError('');
    setSummaryStreaming(true);
    await streamItemsSummary(
      filteredItems.map((i) => i.id),
      (chunk) => setSummaryText((prev) => prev + chunk),
      () => setSummaryStreaming(false),
      (err) => { setSummaryText(''); setSummaryError(err); setSummaryStreaming(false); },
      controller.signal,
    );
  }, [filteredItems]);

  const viewTitle =
    view === 'all' ? 'All Items' :
    view === 'with-me' ? 'Shared with me' :
    view === 'by-me' ? 'Shared by me' :
    shareFolders.find(f => f.id === view)?.name ?? 'Shares';

  const systemViews = [
    { id: 'all', label: 'All Items', icon: null },
    { id: 'with-me', label: 'Shared with me', icon: 'with-me' },
    { id: 'by-me', label: 'Shared by me', icon: 'by-me' },
  ] as const;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left panel */}
      <Box sx={{ width: sidebarOpen ? 220 : 48, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'auto', transition: 'width 0.2s' }}>
        <Box sx={{ p: sidebarOpen ? 1.5 : 0.5, display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'space-between' : 'center' }}>
          <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <IconButton size="small" onClick={() => setSidebarOpen(prev => { const next = !prev; localStorage.setItem('shares_sidebarOpen', String(next)); return next; })}>
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {sidebarOpen && <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, ml: 1 }}>Folders</Typography>}
          {sidebarOpen && (
            <Tooltip title="New folder">
              <IconButton size="small" onClick={async () => {
                const name = prompt('Folder name:');
                if (!name?.trim()) return;
                const f = await createShareFolder(name.trim());
                setShareFolders(prev => [...prev, f].sort((a, b) => a.name.localeCompare(b.name)));
              }}>
                <CreateNewFolderIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <List dense disablePadding>
          {systemViews.map((v) => (
            <ListItem key={v.id} disablePadding>
              <Tooltip title={sidebarOpen ? '' : v.label} placement="right">
                <ListItemButton selected={view === v.id} onClick={() => setView(v.id)} sx={{ borderRadius: 1, mx: 0.5, justifyContent: sidebarOpen ? 'initial' : 'center' }}>
                  <ListItemIcon sx={{ minWidth: sidebarOpen ? 32 : 'auto' }}>
                    {v.icon === 'with-me'
                      ? <SharedWithMeIcon open={view === v.id} />
                      : v.icon === 'by-me'
                        ? <SharedByMeIcon open={view === v.id} />
                        : view === v.id ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                  </ListItemIcon>
                  {sidebarOpen && <ListItemText primary={v.label} primaryTypographyProps={{ variant: 'body2' }} />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
        {sidebarOpen && shareFolders.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <ListItemButton
              onClick={() => setFoldersExpanded(prev => { const next = !prev; localStorage.setItem('shares_foldersExpanded', String(next)); return next; })}
              sx={{ py: 0.5, px: 1.5 }}
            >
              <ListItemText primary="My Folders" primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }} />
              {foldersExpanded ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            </ListItemButton>
            <Collapse in={foldersExpanded} timeout="auto" unmountOnExit>
              <List dense disablePadding>
                {shareFolders.map((f) => (
                  <ListItem key={f.id} disablePadding secondaryAction={
                    <IconButton size="small" edge="end" onClick={(e) => { setFolderMenuAnchor(e.currentTarget); setFolderMenuTarget(f); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  }>
                    <ListItemButton selected={view === f.id} onClick={() => setView(f.id)} sx={{ borderRadius: 1, mx: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {view === f.id ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText primary={f.name} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </Box>

      {/* Right panel */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {view === 'all' ? (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 160, flexGrow: 1 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <ViewModeToggle value={viewMode} onChange={(m) => { setViewMode(m); localStorage.setItem('itemsViewMode', m); }} />
              <IconButton
                onClick={handleSummary}
                title="Summarize"
                size="small"
                disabled={filteredItems.length === 0}
                sx={{ overflow: 'visible' }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <AutoAwesomeIcon />
                  <FolderIcon sx={{ position: 'absolute', bottom: -4, right: -6, fontSize: 14, color: 'text.secondary', zIndex: 1, bgcolor: 'background.paper', borderRadius: '50%', p: '1px' }} />
                </Box>
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Autocomplete
                size="small"
                options={allTags}
                value={filterTag}
                onChange={(_e, v) => setFilterTag(v)}
                renderInput={(params) => <TextField {...params} label="Tag" />}
                sx={{ minWidth: 130 }}
              />
              <Autocomplete
                size="small"
                options={allSites}
                value={filterSite}
                onChange={(_e, v) => setFilterSite(v)}
                renderInput={(params) => <TextField {...params} label="Site" />}
                sx={{ minWidth: 130 }}
              />
              <IconButton
                size="small"
                onClick={() => setFilterStarred(p => !p)}
                sx={{ color: filterStarred ? 'warning.main' : 'action.disabled' }}
                title={filterStarred ? 'Show all' : 'Starred only'}
              >
                <StarIcon />
              </IconButton>
            </Box>

            {loadingItems ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
            ) : items.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                No items yet. Tap + to add your first item.
              </Typography>
            ) : filteredItems.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                No items match your filters.
              </Typography>
            ) : (
              <ItemsView
                items={filteredItems}
                viewMode={viewMode}
                denseGrid
                onClick={(i) => navigate(`/items/${i.id}`)}
                onEdit={(i) => setEditItem(i)}
                onRefresh={async (i) => {
                  await refreshItem(i.id);
                  setItems(prev => prev.map(it => it.id === i.id ? { ...it, enrichmentStatus: 'pending' as const } : it));
                }}
                onShare={(i) => setShareItem(i)}
                onAddToFolder={(i) => setFolderItem(i)}
                onDelete={async (i) => { await deleteItem(i.id); loadItems(); }}
                onToggleStar={(i) => {
                  const newFlags = hasFlag(i.flags, ItemFlags.STARRED) ? i.flags & ~ItemFlags.STARRED : i.flags | ItemFlags.STARRED;
                  setItems(prev => prev.map(it => it.id === i.id ? { ...it, flags: newFlags } : it));
                  updateItemFlags(i.id, newFlags);
                }}
                onTagClick={(tag) => setFilterTag(tag)}
                creatorNames={creatorNames}
              />
            )}

            <Fab color="primary" sx={{ position: 'fixed', bottom: 24, right: 24 }} onClick={() => setCreateOpen(true)}>
              <AddIcon />
            </Fab>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>{viewTitle}</Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
            ) : view === 'with-me' ? (
              withMeEntries.length === 0 ? (
                <Typography color="text.secondary">Nothing has been shared with you yet.</Typography>
              ) : (
                <List disablePadding>
                  {withMeEntries.map((entry) => (
                    <ListItemButton key={entry.id} onClick={() => navigate(`/items/${entry.id}`)} sx={{ borderRadius: 2, mb: 0.5 }}>
                      <ListItemAvatar>
                        {entry.imageUrl
                          ? <Avatar variant="rounded" src={entry.imageUrl} />
                          : <Avatar variant="rounded"><ShareIcon /></Avatar>}
                      </ListItemAvatar>
                      <ListItemText
                        primary={entry.title || entry.id}
                        secondary={`${entry.senderName || entry.senderEmail || 'Unknown'} · ${new Date(entry.sharedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )
            ) : (
              byMeEntries.length === 0 ? (
                <Typography color="text.secondary">
                  {"You haven't shared anything here yet."}
                </Typography>
              ) : (
                <List disablePadding>
                  {byMeEntries.map((entry) => (
                    <ListItemButton
                      key={entry.id}
                      onClick={() => entry.resourceId && navigate(`/items/${entry.resourceId}`)}
                      sx={{ borderRadius: 2, mb: 0.5 }}
                    >
                      <ListItemAvatar>
                        {entry.item?.imageUrl
                          ? <Avatar variant="rounded" src={entry.item.imageUrl} />
                          : <Avatar variant="rounded"><ShareIcon /></Avatar>}
                      </ListItemAvatar>
                      <ListItemText
                        primary={entry.item?.title || entry.resourceId}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" component="span">
                              {new Date(entry.sharedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </Typography>
                            <Chip
                              label={entry.contactName || entry.recipientEmail}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem', width: 'fit-content' }}
                            />
                          </Box>
                        }
                      />
                      {shareFolders.length > 0 && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMoveEntryTarget(entry); }}>
                          <DriveFileMoveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )
            )}
          </>
        )}
      </Box>

      {/* Folder context menu */}
      <Menu anchorEl={folderMenuAnchor} open={Boolean(folderMenuAnchor)} onClose={() => setFolderMenuAnchor(null)}>
        <MenuItem onClick={() => { setRenameFolder(folderMenuTarget); setFolderMenuAnchor(null); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Rename
        </MenuItem>
        <MenuItem onClick={() => { if (folderMenuTarget) handleDeleteFolder(folderMenuTarget); setFolderMenuAnchor(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>Delete
        </MenuItem>
      </Menu>

      {/* Move share to folder */}
      <Menu
        anchorEl={moveEntryTarget ? document.body : null}
        open={Boolean(moveEntryTarget)}
        onClose={() => setMoveEntryTarget(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        transformOrigin={{ vertical: 'center', horizontal: 'center' }}
      >
        <MenuItem disabled><Typography variant="caption">Move to folder</Typography></MenuItem>
        <MenuItem onClick={() => {
          if (moveEntryTarget) moveShareToFolder(moveEntryTarget.id, null).then(loadShareList);
          setMoveEntryTarget(null);
        }}>
          <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon>Remove from folder
        </MenuItem>
        {shareFolders.map(f => (
          <MenuItem key={f.id} onClick={() => {
            if (moveEntryTarget) moveShareToFolder(moveEntryTarget.id, f.id).then(loadShareList);
            setMoveEntryTarget(null);
          }}>
            <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon>{f.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Dialogs */}
      <CreateItemDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => loadItems()} />
      {editItem && (
        <EditItemDialog open onClose={() => setEditItem(null)} item={editItem} onUpdated={loadItems} />
      )}
      {folderItem && (
        <AddToFolderDialog open onClose={() => setFolderItem(null)} item={folderItem} onUpdated={loadItems} />
      )}
      {shareItem && (
        <ShareDialog
          open
          onClose={() => setShareItem(null)}
          title={shareItem.title}
          itemId={shareItem.id}
          currentSharedWith={shareItem.sharedWith}
          onUpdated={() => { loadItems(); loadShareList(); }}
        />
      )}
      {renameFolder && (
        <RenameDialog
          open
          title="Rename folder"
          currentName={renameFolder.name}
          onClose={() => setRenameFolder(null)}
          onSave={async (name) => {
            await updateShareFolder(renameFolder.id, name);
            setRenameFolder(null);
            loadShareFolders();
          }}
        />
      )}

      <Dialog open={summaryOpen} onClose={() => { summaryAbortRef.current?.abort(); setSummaryOpen(false); setSummaryStreaming(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Summary</DialogTitle>
        <DialogContent>
          {summaryStreaming && !summaryText && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {summaryError && (
            <Typography color="error">{summaryError}</Typography>
          )}
          {summaryText && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {summaryText}
            </ReactMarkdown>
          )}
          {summaryStreaming && summaryText && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: '0.6em',
                height: '1em',
                bgcolor: 'text.primary',
                verticalAlign: 'text-bottom',
                animation: 'blink 1s step-end infinite',
                '@keyframes blink': { '50%': { opacity: 0 } },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
